import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import MarketingPageHero from "@/components/marketing/MarketingPageHero";
import { usePageMeta } from "@/lib/usePageMeta";

const POSTS = [
  {
    slug: "why-we-built-leadmato",
    title: "Why we built Leadmato",
    excerpt: "The four painful truths about lead-gen for Indian SMBs that made this product feel inevitable — and how we're solving them one scan at a time.",
    tag: "Founding",
    date: "Coming soon",
  },
  {
    slug: "how-on-demand-social-scans-beat-ad-targeting",
    title: "How on-demand social scans beat ad targeting",
    excerpt: "Ads bet on intent inferred from behaviour. Scans read intent from the words people actually wrote. Here's the maths behind why one pipeline wins.",
    tag: "Playbook",
    date: "Coming soon",
  },
  {
    slug: "indian-business-owners-guide-to-finding-customers-online",
    title: "The Indian business owner's guide to finding customers online",
    excerpt: "A candid walk-through — from setting up your first scan to reading the five lenses — for founders who've never used a lead-gen tool before.",
    tag: "Guide",
    date: "Coming soon",
  },
];

// Flip to false to bring the full blog back online. While true, the page shows a
// "Coming soon" placeholder; all the real blog code below is kept intact.
const BLOG_COMING_SOON = true;

export default function Blog() {
  usePageMeta(
    "Blog — Leadmato",
    "Playbooks, product notes and founder essays from the Leadmato team. Built for Indian SMB owners hunting real customers.",
  );

  if (BLOG_COMING_SOON) {
    return (
      <MarketingLayout>
        <MarketingPageHero
          eyebrow="Blog"
          title="Blog — coming soon."
          subtitle="Playbooks, product notes and founder essays for Indian SMB owners hunting real customers are on the way."
        >
          <div className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-lm-primary bg-lm-tint2 px-3 py-1.5 rounded-full">
            <Clock className="w-4 h-4" /> Coming soon
          </div>
        </MarketingPageHero>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout>
      <MarketingPageHero
        eyebrow="Blog"
        title="Notes from the workshop."
        subtitle="Field notes, playbooks and the occasional product post-mortem."
      />

      <section className="py-12 md:py-16 bg-lm-bg">
        <div className="max-w-6xl mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {POSTS.map((p) => (
            <article
              key={p.slug}
              data-testid={`blog-card-${p.slug}`}
              className="group rounded-2xl bg-lm-surface border border-lm-border p-6 flex flex-col hover:border-lm-primary/40 hover:shadow-sm transition-all"
            >
              <div className="aspect-[16/10] rounded-xl bg-gradient-to-br from-lm-tint2 to-lm-tint mb-5 flex items-center justify-center overflow-hidden">
                <div className="text-6xl font-display font-bold text-lm-primary/25 select-none">L</div>
              </div>
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-lm-text3">
                <span className="px-1.5 py-0.5 rounded bg-lm-tint2 text-lm-primary">{p.tag}</span>
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{p.date}</span>
              </div>
              <h3 className="mt-3 font-display text-xl font-semibold text-lm-text tracking-tight leading-snug">
                {p.title}
              </h3>
              <p className="mt-3 text-sm text-lm-text2 leading-relaxed flex-1">{p.excerpt}</p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-lm-primary/70">
                Coming soon
              </div>
            </article>
          ))}
        </div>

        <div className="mt-14 max-w-2xl mx-auto px-5 md:px-10 text-center">
          <div className="rounded-2xl border border-lm-border bg-lm-surface p-8">
            <h3 className="font-display text-xl font-semibold text-lm-text">Want it in your inbox?</h3>
            <p className="mt-2 text-sm text-lm-text2">The first three essays land in the next few weeks. Get on the launch list.</p>
            <Link
              to="/signup"
              data-testid="blog-cta-signup"
              className="mt-5 inline-flex items-center gap-2 bg-lm-primary hover:bg-lm-primaryHover text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-all"
            >
              Get on the list <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
